'use client';

import { forwardRef } from 'react';
import { clsx } from 'clsx';
import ArrowLabel from './ArrowLabel';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  /** Show an arrow that slides in on hover — opt-in for marketing CTAs. */
  arrow?: boolean;
  /** Show a spinner + disable the button while an async action is in flight. */
  loading?: boolean;
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'primary', size = 'md', fullWidth = false, arrow = false, loading = false, disabled, className, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={clsx(
          'inline-flex items-center justify-center rounded-full font-headline font-bold transition-all duration-200 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          // While loading, keep full opacity so the gold effect stays visible;
          // only a real disabled state dims the button. Both block clicks.
          loading ? 'pointer-events-none' : 'disabled:opacity-50 disabled:pointer-events-none',
          arrow && !loading && 'group',
          {
            'bg-on-surface text-background hover:opacity-90': variant === 'primary',
            'bg-transparent border border-on-surface text-on-surface hover:bg-on-surface/10': variant === 'secondary',
            'px-4 py-2 text-label-caps': size === 'sm',
            'px-6 py-4 text-body-md': size === 'md',
            'px-8 py-5 text-body-md': size === 'lg',
            'w-full': fullWidth,
          },
          className
        )}
        {...props}
      >
        {loading ? (
          <span className="inline-flex items-center gap-2">
            <Spinner />
            {children}
          </span>
        ) : arrow ? (
          <ArrowLabel>{children}</ArrowLabel>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
